import React, { useEffect, useRef } from "react";
import {
  Box,
  FormControl,
  MenuItem,
  Select,
  type SelectChangeEvent,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import WaveSurfer from "wavesurfer.js";
import {
  PauseActiveLightIcon,
  PlayActiveLightIcon,
  PlayIconActiveDark,
} from "../../../../assets/icon";
import { fmtTs } from "../../../../utils/transcription";
import { loadWaveSurferAudio } from "../../../../utils/waveSurferAudioLoad";
import { useAuthenticatedFileUrl } from "../../../../hooks/useAuthenticatedFileUrl";
import { useAppStore } from "../../../../stores/appStore";
import {
  RING_ROTATION_DEG,
  RING_SIZE,
  RING_STROKE,
  RING_VISIBLE_ARC,
  overviewAvatarStyles,
  overviewCardStyles,
  overviewDoctorNameStyles,
  overviewHeaderRowStyles,
  overviewLandscapeIdentityColumnStyles,
  overviewLandscapePlayerRowStyles,
  overviewLandscapeTransportColumnStyles,
  overviewLessonTitleStyles,
  overviewPlayButtonStyles,
  overviewProgressSvgStyles,
  overviewRingContainerStyles,
  overviewSectionTitleStyles,
  overviewSeekRowStyles,
  overviewSpeedFormStyles,
  overviewSpeedSelectStyles,
  overviewTimeRowLandscapeInlineStyles,
  overviewTimeRowStyles,
  overviewTimeTextStyles,
  MODAL_LANDSCAPE_COMPACT_QUERY,
  ringTrackColor,
} from "./styles";

export interface AudioOverviewPanelProps {
  overviewAvatar: string;
  overviewDoctorName: string;
  overviewTitle: string;
  displayCurrentSec: number;
  duration: number;
  progress: number;
  currentTimeSec: number;
  audioUrl?: string;
  isPlaying: boolean;
  onPlayPause?: () => void;
  onSeekToSecond?: (sec: number) => void;
  playbackRate?: number;
  onPlaybackRateChange?: (rate: number) => void;
}

export const AudioOverviewPanel = React.forwardRef<
  HTMLDivElement,
  AudioOverviewPanelProps
>(function AudioOverviewPanel(
  {
    overviewAvatar,
    overviewDoctorName,
    overviewTitle,
    displayCurrentSec,
    duration,
    progress,
    currentTimeSec,
    audioUrl,
    isPlaying,
    onPlayPause,
    onSeekToSecond,
    playbackRate = 1,
    onPlaybackRateChange,
  },
  ref,
) {
  const { url: resolvedAudioUrl } = useAuthenticatedFileUrl(audioUrl);
  const effectiveAudioUrl = resolvedAudioUrl ?? audioUrl;
  const { url: resolvedOverviewAvatar } =
    useAuthenticatedFileUrl(overviewAvatar);
  const effectiveOverviewAvatar = resolvedOverviewAvatar ?? overviewAvatar;
  const theme = useTheme();
  const { isDark } = useAppStore();
  const isTabletLayout = useMediaQuery(theme.breakpoints.between("sm", "md"));
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isCompactLandscape = useMediaQuery(MODAL_LANDSCAPE_COMPACT_QUERY);
  const waveformContainerRef = useRef<HTMLDivElement | null>(null);
  const waveSurferRef = useRef<WaveSurfer | null>(null);
  const isDraggingRef = useRef(false);
  const onSeekRef = useRef(onSeekToSecond);
  onSeekRef.current = onSeekToSecond;
  const playbackRateRef = useRef(playbackRate);
  playbackRateRef.current = playbackRate;
  const durationRef = useRef(duration);
  durationRef.current = duration;
  const currentTimeRef = useRef(currentTimeSec);
  currentTimeRef.current = currentTimeSec;

  const waveHeight = isCompactLandscape ? 30 : isMobile ? 44 : 64;
  const playIconSize = isCompactLandscape ? 40 : isTabletLayout ? 56 : 68;

  const ringRadius = (RING_SIZE - RING_STROKE) / 2;
  const ringCirc = 2 * Math.PI * ringRadius;
  const arcLen = ringCirc * RING_VISIBLE_ARC;
  const gapLen = ringCirc - arcLen;

  const onSpeed = (e: SelectChangeEvent<number>) => {
    onPlaybackRateChange?.(Number(e.target.value));
  };

  useEffect(() => {
    if (!effectiveAudioUrl || !waveformContainerRef.current) {
      return;
    }

    const ws = WaveSurfer.create({
      container: waveformContainerRef.current,
      waveColor: theme.palette.audioPlayer.waveColorEnd,
      progressColor: theme.palette.audioPlayer.waveColorStart,
      cursorWidth: 2,
      cursorColor: "#E6E6E680",
      height: waveHeight,
      barWidth: 3,
      barGap: 3,
      barRadius: 2,
      backend: "MediaElement",
      fillParent: true,
      dragToSeek: !!onSeekToSecond,
      interact: !!onSeekToSecond,
    });

    waveSurferRef.current = ws;

    const onInteraction = (t: number) => {
      onSeekRef.current?.(t);
    };
    const onDragStart = () => {
      isDraggingRef.current = true;
    };
    const onDragEnd = () => {
      isDraggingRef.current = false;
    };

    ws.on("interaction", onInteraction);
    ws.on("dragstart", onDragStart);
    ws.on("dragend", onDragEnd);

    loadWaveSurferAudio(ws, effectiveAudioUrl).catch((err: unknown) => {
      if ((err as DOMException)?.name !== "AbortError") {
        console.warn("Overview waveform failed to load:", err);
      }
    });

    ws.once("ready", () => {
      const el = ws.getMediaElement?.() as HTMLAudioElement | undefined;
      if (el) {
        el.muted = true;
        el.volume = 0;
      }
      ws.setPlaybackRate(playbackRateRef.current);
      const d = durationRef.current;
      if (d > 0) {
        const t = currentTimeRef.current;
        ws.seekTo(Math.max(0, Math.min(1, Math.max(0, t) / d)));
      }
    });

    return () => {
      ws.un("interaction", onInteraction);
      ws.un("dragstart", onDragStart);
      ws.un("dragend", onDragEnd);
      ws.destroy();
      waveSurferRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- onSeekToSecond wired via onSeekRef
  }, [
    effectiveAudioUrl,
    waveHeight,
    theme.palette.audioPlayer.waveColorEnd,
    theme.palette.audioPlayer.waveColorStart,
    theme.palette.mode,
    isCompactLandscape,
  ]);

  useEffect(() => {
    waveSurferRef.current?.setPlaybackRate(playbackRate);
  }, [playbackRate]);

  useEffect(() => {
    const ws = waveSurferRef.current;
    if (!ws || duration <= 0 || isDraggingRef.current) return;
    ws.seekTo(Math.max(0, Math.min(1, currentTimeSec / duration)));
  }, [currentTimeSec, duration]);

  const ringAndPlay = (
    <Box sx={overviewRingContainerStyles}>
      <Box
        component="svg"
        viewBox={`0 0 ${RING_SIZE} ${RING_SIZE}`}
        sx={overviewProgressSvgStyles(RING_ROTATION_DEG)}
      >
        <circle
          cx={RING_SIZE / 2}
          cy={RING_SIZE / 2}
          r={ringRadius}
          fill="none"
          stroke={ringTrackColor(theme)}
          strokeWidth={RING_STROKE}
          strokeLinecap="round"
          strokeDasharray={`${arcLen} ${gapLen}`}
        />
        <circle
          cx={RING_SIZE / 2}
          cy={RING_SIZE / 2}
          r={ringRadius}
          fill="none"
          stroke={theme.palette.audioPlayer.waveColorStart}
          strokeWidth={RING_STROKE}
          strokeLinecap="round"
          strokeDasharray={`${arcLen * progress} ${ringCirc}`}
        />
      </Box>

      <Box
        component="button"
        onClick={onPlayPause}
        sx={overviewPlayButtonStyles(!!onPlayPause)}
      >
        {isPlaying ? (
          <PauseActiveLightIcon
            style={{ width: playIconSize, height: playIconSize }}
          />
        ) : isDark ? (
          <PlayIconActiveDark
            style={{ width: playIconSize, height: playIconSize }}
          />
        ) : (
          <PlayActiveLightIcon
            style={{ width: playIconSize, height: playIconSize }}
          />
        )}
      </Box>
    </Box>
  );

  const timeTypography = (
    <Typography variant="caption" sx={overviewTimeTextStyles(theme)}>
      {fmtTs(displayCurrentSec)} / {duration > 0 ? fmtTs(duration) : "--:--"}
    </Typography>
  );

  return (
    <Box ref={ref} sx={overviewCardStyles(theme)}>
      <Box sx={overviewHeaderRowStyles}>
        <Typography variant="subtitle1" sx={overviewSectionTitleStyles(theme)}>
          Audio Overview
        </Typography>

        {onPlaybackRateChange ? (
          <FormControl size="small" sx={overviewSpeedFormStyles}>
            <Select
              value={playbackRate}
              onChange={onSpeed}
              sx={overviewSpeedSelectStyles(theme)}
            >
              {[0.5, 1, 1.5, 2].map((v) => (
                <MenuItem key={v} value={v} sx={{ fontSize: 14 }}>
                  {v}x
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ) : null}
      </Box>

      {isCompactLandscape ? (
        <Box sx={overviewLandscapePlayerRowStyles}>
          <Box sx={overviewLandscapeIdentityColumnStyles}>
            <Box
              component="img"
              src={effectiveOverviewAvatar}
              alt="audio-only-avatar"
              sx={overviewAvatarStyles(theme)}
            />
            <Typography
              variant="subtitle2"
              component="div"
              sx={overviewDoctorNameStyles(theme)}
            >
              {overviewDoctorName}
            </Typography>
          </Box>
          <Box sx={overviewLandscapeTransportColumnStyles}>
            {ringAndPlay}
            <Box sx={overviewTimeRowLandscapeInlineStyles}>{timeTypography}</Box>
          </Box>
        </Box>
      ) : (
        <>
          <Box
            component="img"
            src={effectiveOverviewAvatar}
            alt="audio-only-avatar"
            sx={overviewAvatarStyles(theme)}
          />

          <Typography variant="h6" sx={overviewDoctorNameStyles(theme)}>
            {overviewDoctorName}
          </Typography>

          {ringAndPlay}

          <Box sx={overviewTimeRowStyles}>{timeTypography}</Box>
        </>
      )}

      <Typography variant="body2" sx={overviewLessonTitleStyles(theme)}>
        {overviewTitle}
      </Typography>

      {effectiveAudioUrl ? (
        <Box
          sx={{
            ...overviewSeekRowStyles,
            cursor: onSeekToSecond ? "pointer" : "default",
          }}
        >
          <Box
            ref={waveformContainerRef}
            sx={{ flex: 1, minWidth: 0, width: "100%" }}
          />
        </Box>
      ) : null}
    </Box>
  );
});
